/**
 * i18n bootstrap. Imported once from src/main.tsx before <App> renders.
 *
 * Phase 0 ships English-only. Resources are inline so the bundle works offline
 * (PWA) without a network round-trip on first paint. As more locales ship,
 * we'll lazy-load namespaces from /public/locales/{lng}/{ns}.json.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import { DEFAULT_LOCALE, FALLBACK_LOCALE, NAMESPACES, isRTL, PSEUDO_LOCALE } from "./config";
import enCommon from "./locales/en/common.json";
import esCommon from "./locales/es/common.json";
import ptBRCommon from "./locales/pt-BR/common.json";
import frCommon from "./locales/fr/common.json";
import jaCommon from "./locales/ja/common.json";
import itCommon from "./locales/it/common.json";
import arCommon from "./locales/ar/common.json";

import enRsvp from "./locales/en/rsvp.json";
import esRsvp from "./locales/es/rsvp.json";
import ptBRRsvp from "./locales/pt-BR/rsvp.json";
import frRsvp from "./locales/fr/rsvp.json";
import jaRsvp from "./locales/ja/rsvp.json";
import itRsvp from "./locales/it/rsvp.json";
import arRsvp from "./locales/ar/rsvp.json";

import enCoach from "./locales/en/coach.json";
import esCoach from "./locales/es/coach.json";
import ptBRCoach from "./locales/pt-BR/coach.json";
import frCoach from "./locales/fr/coach.json";
import jaCoach from "./locales/ja/coach.json";
import itCoach from "./locales/it/coach.json";
import arCoach from "./locales/ar/coach.json";

import enFillin from "./locales/en/fillin.json";
import esFillin from "./locales/es/fillin.json";
import ptBRFillin from "./locales/pt-BR/fillin.json";
import frFillin from "./locales/fr/fillin.json";
import jaFillin from "./locales/ja/fillin.json";
import itFillin from "./locales/it/fillin.json";
import arFillin from "./locales/ar/fillin.json";

/**
 * Pseudo-locale generator — wraps every string in [!! …!!] for QA.
 * Triggered by `?lang=qps-ploc`.
 */
function pseudo(value: unknown): unknown {
  if (typeof value === "string") return `[!!${value}!!]`;
  if (Array.isArray(value)) return value.map(pseudo);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = pseudo(v);
    return out;
  }
  return value;
}

const enResources = {
  common: enCommon,
  rsvp: enRsvp,
  coach: enCoach,
  fillin: enFillin,
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: ["en", "es", "pt-BR", "fr", "ja", "it", "ar", PSEUDO_LOCALE],
    ns: [...NAMESPACES],
    defaultNS: "common",
    resources: {
      en: enResources,
      es: { common: esCommon, rsvp: esRsvp, coach: esCoach, fillin: esFillin },
      "pt-BR": { common: ptBRCommon, rsvp: ptBRRsvp, coach: ptBRCoach, fillin: ptBRFillin },
      fr: { common: frCommon, rsvp: frRsvp, coach: frCoach, fillin: frFillin },
      ja: { common: jaCommon, rsvp: jaRsvp, coach: jaCoach, fillin: jaFillin },
      it: { common: itCommon, rsvp: itRsvp, coach: itCoach, fillin: itFillin },
      ar: { common: arCommon, rsvp: arRsvp, coach: arCoach, fillin: arFillin },
      [PSEUDO_LOCALE]: pseudo(enResources) as typeof enResources,
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Order: explicit URL param > stored preference > browser > html lang > default
      order: ["querystring", "localStorage", "navigator", "htmlTag"],
      lookupQuerystring: "lang",
      lookupLocalStorage: "vg.locale",
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false, // we ship resources inline, no Suspense fallback needed
    },
  });

/** Keep <html lang> and dir in sync with the active locale. */
function syncDocumentLang(lng: string) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lng;
  document.documentElement.dir = isRTL(lng) ? "rtl" : "ltr";
}

i18n.on("languageChanged", syncDocumentLang);
syncDocumentLang(i18n.language || DEFAULT_LOCALE);

export default i18n;
